import { Controller } from "@hotwired/stimulus"

// Makes a server-rendered error summary announce itself.
//
// The form posts to a URL carrying the #rsvp fragment, so the browser lands on
// the summary natively, before first paint. Everything here therefore waits for
// that to finish, for two reasons:
//
//   1. The native jump can land *after* this controller connects. Measuring the
//      position early would read "off-screen", scroll, and then get scrolled
//      again by the browser — the visible double-jump we are avoiding.
//   2. A fragment navigation resets focus to <body>, so focus taken during
//      connect is silently thrown away.
//
// Focus is what makes a screen reader announce the rejection; preventScroll
// keeps taking it from moving the viewport a second time. The scroll is only a
// fallback for when the native jump did not happen at all.
export default class extends Controller {
  connect() {
    this.settle = () => {
      if (!this.inView) this.element.scrollIntoView({ block: "center", behavior: "auto" })
      this.element.focus({ preventScroll: true })
    }

    if (document.readyState === "complete") {
      requestAnimationFrame(this.settle)
    } else {
      this.onLoad = () => requestAnimationFrame(this.settle)
      window.addEventListener("load", this.onLoad, { once: true })
    }
  }

  disconnect() {
    if (this.onLoad) window.removeEventListener("load", this.onLoad)
  }

  get inView() {
    const box = this.element.getBoundingClientRect()

    // Comfortably on screen rather than strictly contained: a tall summary may
    // run past the fold and still be perfectly readable where it sits.
    return box.bottom > 0 && box.top < window.innerHeight * 0.8
  }
}
